/**
 * Bulk photo upload — docs/08-frontend-and-ux.md §6.3.
 *
 * This is the surface built for the person digitising a shoebox, not for
 * somebody posting one picture. Three things follow from that, and all three are
 * about volume:
 *
 *  - **Three at a time, each with its own progress and its own retry.** One
 *    failed file out of forty must not cost the other thirty-nine, and a 14 MB
 *    scan over a domestic uplink needs a bar that moves or it reads as a hang.
 *  - **"Apply to all".** Forty photos from the same wedding share a date, a
 *    caption prefix and a cast; typing that forty times is how a batch gets
 *    abandoned half-tagged, which is worse than untagged.
 *  - **A duplicate is good news, said as such.** The server dedupes on the
 *    digest, so re-uploading a folder is safe. That deserves a calm "already in
 *    the album", not an error.
 */

import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { FuzzyDateParts, PersonSummary } from '@famtree/shared';
import { isFuzzyDateEmpty } from '@famtree/shared';
import { photosApi, ApiError } from '../../api/client.js';
import { FuzzyDateField } from '../../ui/FuzzyDateField.js';
import { PersonPicker } from '../people/PersonPicker.js';
import { personName } from '../people/labels.js';
import { problemMessage } from '../../ui/problem.js';
import ui from '../../ui/components.module.css';
import styles from './photos.module.css';

/** docs/08 §6.3 — three concurrent uploads; more just fight for the same uplink. */
const CONCURRENCY = 3;

type ItemState = 'queued' | 'uploading' | 'done' | 'duplicate' | 'failed';

interface QueueItem {
  key: string;
  file: File;
  state: ItemState;
  progress: number;
  photoId?: string;
  error?: string;
}

export function PhotoUploader({
  /** Pre-tagged subject, when the uploader was opened from someone's gallery. */
  defaultSubject,
  onUploaded,
}: {
  defaultSubject?: PersonSummary | null;
  onUploaded?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // "Apply to all" — set once, sent with every file in the batch.
  const [taken, setTaken] = useState<FuzzyDateParts | null>(null);
  const [captionPrefix, setCaptionPrefix] = useState('');
  const [subjects, setSubjects] = useState<PersonSummary[]>(defaultSubject ? [defaultSubject] : []);
  const [pendingSubject, setPendingSubject] = useState<PersonSummary | null>(null);

  const patch = useCallback((key: string, changes: Partial<QueueItem>): void => {
    setItems((all) => all.map((item) => (item.key === key ? { ...item, ...changes } : item)));
  }, []);

  const uploadOne = useCallback(
    async (item: QueueItem): Promise<void> => {
      patch(item.key, { state: 'uploading', progress: 0 });
      try {
        const result = await photosApi.upload(
          item.file,
          {
            ...(captionPrefix.trim() ? { caption: captionPrefix.trim() } : {}),
            ...(taken && !isFuzzyDateEmpty(taken) ? { taken } : {}),
            ...(subjects.length > 0
              ? { subjects: subjects.map((person) => ({ person_id: person.id })) }
              : {}),
          },
          (fraction) => patch(item.key, { progress: fraction }),
        );
        patch(item.key, {
          state: result.deduplicated ? 'duplicate' : 'done',
          progress: 1,
          photoId: result.photo.id,
        });
      } catch (error) {
        patch(item.key, {
          state: 'failed',
          error: error instanceof ApiError ? problemMessage(error, t) : String(error),
        });
      }
    },
    [captionPrefix, patch, subjects, t, taken],
  );

  /** A small worker pool: `CONCURRENCY` uploads in flight, the rest waiting. */
  const run = useCallback(
    async (queued: QueueItem[]): Promise<void> => {
      const pending = [...queued];
      const workers = Array.from({ length: Math.min(CONCURRENCY, pending.length) }, async () => {
        for (let next = pending.shift(); next; next = pending.shift()) {
          await uploadOne(next);
        }
      });
      await Promise.all(workers);
      onUploaded?.();
    },
    [onUploaded, uploadOne],
  );

  const accept = useCallback(
    (files: FileList | null): void => {
      if (!files || files.length === 0) return;
      const added: QueueItem[] = Array.from(files).map((file, index) => ({
        key: `${file.name}-${String(file.size)}-${String(Date.now())}-${String(index)}`,
        file,
        state: 'queued',
        progress: 0,
      }));
      setItems((all) => [...all, ...added]);
      void run(added);
    },
    [run],
  );

  const busy = items.some((item) => item.state === 'uploading' || item.state === 'queued');

  return (
    <div>
      <section className={ui.card}>
        <h2>{t('photos.applyToAll')}</h2>
        <p className={ui.muted}>{t('photos.applyToAllHelp')}</p>

        <FuzzyDateField
          legend={t('photos.taken')}
          value={taken}
          onChange={setTaken}
          allowRelative={false}
        />

        <label className={ui.field}>
          <span className={ui.label}>{t('photos.caption')}</span>
          <input
            className={ui.input}
            maxLength={500}
            value={captionPrefix}
            onChange={(event) => setCaptionPrefix(event.target.value)}
          />
        </label>

        <div className={ui.field}>
          <span className={ui.label}>{t('photos.people')}</span>
          {subjects.map((person) => (
            <span key={person.id} className={styles.subjectRow}>
              {personName(person)}{' '}
              <button
                type="button"
                className={ui.buttonSecondary}
                onClick={() => setSubjects((all) => all.filter((p) => p.id !== person.id))}
              >
                {t('photos.removeTag')}
              </button>
            </span>
          ))}
          <PersonPicker
            label={t('photos.addPerson')}
            value={pendingSubject}
            onChange={(person) => {
              if (person && !subjects.some((existing) => existing.id === person.id)) {
                setSubjects((all) => [...all, person]);
              }
              // The picker is a one-shot: chosen, added to the list, cleared.
              setPendingSubject(null);
            }}
          />
        </div>
      </section>

      <div
        className={dragging ? styles.dropZoneActive : styles.dropZone}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          accept(event.dataTransfer.files);
        }}
      >
        <p>{t('photos.dropHere')}</p>
        {/* Drag is an accelerator, never the only path (docs/08 §6.2, §10). */}
        <button type="button" className={ui.button} onClick={() => inputRef.current?.click()}>
          {t('photos.browse')}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/heic"
          hidden
          onChange={(event) => {
            accept(event.target.files);
            event.target.value = '';
          }}
        />
        <p className={ui.muted}>{t('photos.limits')}</p>
      </div>

      {items.length > 0 && (
        <ul className={styles.queue}>
          {items.map((item) => (
            <li key={item.key} className={styles.queueRow}>
              <span className={styles.fileName}>{item.file.name}</span>
              <span className={styles.spacer} />
              {item.state === 'uploading' && (
                <span
                  className={styles.progress}
                  role="progressbar"
                  aria-valuenow={Math.round(item.progress * 100)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={item.file.name}
                >
                  <span
                    className={styles.progressBar}
                    style={{ inlineSize: `${String(Math.round(item.progress * 100))}%` }}
                  />
                </span>
              )}
              <span className={styles.status}>{t(`photos.states.${item.state}`)}</span>
              {item.state === 'failed' && (
                <>
                  <span className={ui.problem}>{item.error}</span>
                  <button
                    type="button"
                    className={ui.buttonSecondary}
                    onClick={() => void uploadOne(item)}
                  >
                    {t('app.retry')}
                  </button>
                </>
              )}
              {item.photoId && <Link to={`/photos/${item.photoId}`}>{t('photos.view')}</Link>}
            </li>
          ))}
        </ul>
      )}

      {busy && <p className={ui.muted}>{t('photos.uploading')}</p>}
    </div>
  );
}
